/** Console v0.5: accepting an invite (/invite#<token>; session A's routes, #367). No session yet: the token in the
 * link's fragment is the secret, held in memory only. The person chooses a password here, then sets up an authenticator app on the same screen as a
 * first sign-in (auth.ts), and only then signs in -- nothing of the account is shown before that (brief s.6). */

import { ApiError } from "../api.js";
import { showAuthenticatorSetup } from "../auth.js";
import { acceptInvite, fetchInviteInfo } from "./team-api.js";
import { PASSWORD_RULES, inviteDeadEnd, passwordMissing } from "./team-logic.js";
import { h, uid } from "./ui.js";

const FOOTER = "Preekie is a product of CPAI Australia Pty Ltd · ACN 674 037 668";

/** The sign-in card's frame (the wordmark belongs on sign-in surfaces, PRODUCT-UI.md s.2). */
function frame(root: HTMLElement, heading: string, lead?: string): HTMLElement {
  const wrap = h("div", "pk-auth-card");
  const logo = h("img", "pk-auth-wordmark");
  logo.src = "/brand/preekie-wordmark-ink.svg";
  logo.alt = "Preekie";
  logo.width = 112;
  logo.height = 32;
  const title = h("h1", "pk-auth-heading", heading);
  title.tabIndex = -1;
  wrap.append(logo, title);
  if (lead) wrap.append(h("p", "pk-auth-lead", lead));
  root.replaceChildren(wrap, h("p", "pk-auth-footer", FOOTER));
  title.focus();
  return wrap;
}

function deadEnd(root: HTMLElement, err: unknown): void {
  const d = err instanceof ApiError ? inviteDeadEnd(err.status, err.code) : { title: "Preekie couldn't be reached", body: "Check your connection, then open the link again." };
  const wrap = frame(root, d.title, d.body);
  const signIn = h("a", "pk-auth-link", "Go to sign in");
  signIn.href = "/";
  wrap.append(signIn);
}

export async function showInviteAccept(root: HTMLElement, token: string): Promise<void> {
  root.hidden = false;
  document.title = "Accept your invite · Preekie";
  if (!token) {
    // No usable token in the fragment (or it was already removed, e.g. by a reload).
    deadEnd(root, new ApiError("", 404, null));
    return;
  }
  frame(root, "Opening your invite…");
  let info;
  try {
    info = await fetchInviteInfo(token);
  } catch (err) {
    deadEnd(root, err);
    return;
  }
  const wrap = frame(root, `Join ${info.account_name} on Preekie`, "Choose a password. Next, you'll set up an authenticator app, then sign in.");
  const form = h("form", "pk-auth-form");
  form.noValidate = true;

  const field = (label: string, input: HTMLInputElement, describedBy?: string) => {
    const id = uid("pk5-accept");
    input.id = id;
    input.className = "pk-auth-input";
    if (describedBy) input.setAttribute("aria-describedby", describedBy);
    const l = h("label", "pk-auth-label", label);
    l.htmlFor = id;
    form.append(l, input);
  };

  const email = h("input");
  email.type = "email";
  email.value = info.email;
  email.readOnly = true;
  email.autocomplete = "username";
  field("Email", email);

  const name = h("input");
  name.type = "text";
  name.autocomplete = "name";
  name.maxLength = 120;
  field("Your name", name);

  const rulesId = uid("pk5-rules");
  const rules = h("ul", "pk5-rules");
  rules.id = rulesId;
  const pw = h("input");
  pw.type = "password";
  pw.autocomplete = "new-password";
  field("Password", pw, rulesId);
  form.append(rules);
  const again = h("input");
  again.type = "password";
  again.autocomplete = "new-password";
  field("Password again", again);

  const drawRules = () => {
    rules.replaceChildren();
    for (const r of PASSWORD_RULES) {
      const met = r.ok(pw.value);
      // The words say it, not only the mark (colour never carries meaning alone).
      rules.append(h("li", met ? "pk5-rule pk5-rule-met" : "pk5-rule", met ? `✓ ${r.label}` : `○ ${r.label}`, h("span", "pk-visually-hidden", met ? " (done)" : " (still needed)")));
    }
  };
  pw.addEventListener("input", drawRules);
  drawRules();

  const error = h("p", "pk-auth-error");
  error.setAttribute("role", "alert");
  error.hidden = true;
  const submit = h("button", "btn btn-primary pk-auth-submit", "Set password and continue");
  submit.type = "submit";
  form.append(submit);
  wrap.append(form, error);

  const fail = (text: string) => {
    error.textContent = text;
    error.hidden = false;
  };

  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    const missing = passwordMissing(pw.value);
    if (missing.length) return fail(`Your password still needs: ${missing.join(", ").toLowerCase()}.`);
    if (pw.value !== again.value) return fail("The two passwords don't match.");
    submit.disabled = true;
    error.hidden = true;
    void acceptInvite(token, pw.value, name.value)
      .then((setup) => {
        pw.value = "";
        again.value = "";
        // The same authenticator screen as a first sign-in; once it's verified the session exists and "/" runs the
        // terms step, then the console.
        showAuthenticatorSetup(root, info.email, setup, () => location.assign("/"));
      })
      .catch((err: unknown) => {
        submit.disabled = false;
        if (err instanceof ApiError && err.status === 400) return fail(err.message); // invalid_new_password: same link still works
        if (err instanceof ApiError && (err.status === 410 || err.status === 409 || err.status === 503 || err.status === 404)) return deadEnd(root, err);
        fail(err instanceof ApiError ? err.message : "Preekie couldn't be reached. Check your connection and try again.");
      });
  });
  name.focus();
}
